import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PhoneCall, Check, ExternalLink, AlarmClock } from "lucide-react";
import { useCobrancaCliente } from "../hooks/useCobrancaCliente";
import { registrarCobranca } from "../services/cobranca";
import { tempoParadoLabel } from "../utils/cobrancaCliente";

const SNOOZE_KEY = "recdc:cobranca-snooze";
const SNOOZE_MS = 3 * 60 * 60 * 1000; // "Depois" adia 3h; volta no próximo tick

/**
 * MELHORIA 13 — lembrete BLOQUEANTE (estilo o do play) que para o atendimento
 * pra cobrar o cliente nos cards de Demandas / Aguardando cliente. Lista os cards
 * que precisam de cobrança HOJE; a pessoa clica "Registrar cobrança" em cada um
 * (silencia até amanhã) ou "Depois" (adia 3h). Cards que passaram de 48h vêm em
 * vermelho. Como é diário, reaparece no dia seguinte (o backend zera à meia-noite).
 */
export function LembreteCobrancaCliente() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: pendencias = [] } = useCobrancaCliente();
  const [snoozeAte, setSnoozeAte] = useState<number>(() => {
    const v = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return Number.isFinite(v) ? v : 0;
  });

  const registrarM = useMutation({
    mutationFn: (id: string) => registrarCobranca(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cobranca-pendentes"] });
      qc.invalidateQueries({ queryKey: ["tarefas"] });
    },
    onError: () => alert("Não foi possível registrar a cobrança agora. Tente de novo."),
  });

  function adiar() {
    const ate = Date.now() + SNOOZE_MS;
    localStorage.setItem(SNOOZE_KEY, String(ate));
    setSnoozeAte(ate);
  }

  const aCobrar = pendencias.filter((c) => c.precisaCobrarHoje);
  if (aCobrar.length === 0) return null;
  if (Date.now() < snoozeAte) return null;

  const estourados = aCobrar.filter((c) => c.slaEstourado).length;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl border-2 border-amber-300 dark:border-amber-700 overflow-hidden flex flex-col max-h-[85vh]">
        <div className="bg-amber-50 dark:bg-amber-900/30 px-6 py-4 flex items-center gap-3 border-b border-amber-200 dark:border-amber-800 shrink-0">
          <div className="w-11 h-11 rounded-full bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center shrink-0">
            <PhoneCall size={22} className="text-amber-600 dark:text-amber-300" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Cobre os clientes de hoje</h2>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {aCobrar.length} card(s) esperando retorno do cliente
              {estourados > 0 && <> · <span className="font-bold text-red-600 dark:text-red-400">{estourados} passou de 48h</span></>}
            </p>
          </div>
        </div>

        <div className="px-4 py-3 overflow-y-auto space-y-2">
          <p className="text-xs text-slate-500 dark:text-slate-400 px-2">
            A meta é não deixar card parado mais de 48h. Ao falar com o cliente, clique em <strong>Registrar cobrança</strong> — some da lista até amanhã.
          </p>
          {aCobrar.map((c) => (
            <div
              key={c.id}
              className={`rounded-lg border p-2.5 ${c.slaEstourado
                ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100 line-clamp-2">{c.titulo}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    {c.cliente ? `${c.cliente} · ` : ""}{c.projetoNome || "sem projeto"} · {c.colunaEmoji} {c.colunaLabel}
                  </p>
                </div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${c.slaEstourado
                  ? "bg-red-200 text-red-800 dark:bg-red-800/60 dark:text-red-200"
                  : "bg-amber-200 text-amber-800 dark:bg-amber-800/60 dark:text-amber-200"}`}>
                  há {tempoParadoLabel(c.paradoMs)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => registrarM.mutate(c.id)}
                  disabled={registrarM.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                >
                  <Check size={14} /> Registrar cobrança
                </button>
                <button
                  onClick={() => { adiar(); navigate(`/tarefas?tarefa=${c.id}`); }}
                  className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600"
                  title="Abrir o card"
                >
                  <ExternalLink size={14} /> Abrir
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700 shrink-0">
          <button
            onClick={adiar}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50"
          >
            <AlarmClock size={14} /> Depois (lembrar em 3h)
          </button>
        </div>
      </div>
    </div>
  );
}
